import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  tenantApi,
  adminUserApi,
  type Tenant,
  type CreateTenantRequest,
  type CreateUserRequest,
} from '../../services/adminService';

type Tab = 'tenants' | 'create-user';

export default function SuperAdminPanel() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('tenants');

  // ===== TENANT STATE =====
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [tenantForm, setTenantForm] = useState<CreateTenantRequest>({
    name: '',
    nit: '',
    active: true,
  });
  const [tenantError, setTenantError] = useState('');
  const [tenantSuccess, setTenantSuccess] = useState('');
  const [submittingTenant, setSubmittingTenant] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // ===== USER STATE =====
  const [userForm, setUserForm] = useState<CreateUserRequest>({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    tenantId: '',
    roleId: 1,
  });
  const [userError, setUserError] = useState('');
  const [userSuccess, setUserSuccess] = useState('');
  const [submittingUser, setSubmittingUser] = useState(false);

  // ===== LOAD TENANTS =====
  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    setLoadingTenants(true);
    try {
      const res = await tenantApi.getAll();
      setTenants(res.tenants || []);
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : 'Error al cargar tenants');
    } finally {
      setLoadingTenants(false);
    }
  };

  // ===== TENANT ACTIONS =====
  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setTenantError('');
    setTenantSuccess('');
    setSubmittingTenant(true);
    try {
      const created = await tenantApi.create(tenantForm);
      setTenantSuccess(`✅ Tenant "${created.name}" creado (ID: ${created.id.slice(0, 8)}...)`);
      setTenantForm({ name: '', nit: '', active: true });
      setShowCreateTenant(false);
      loadTenants();
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : 'Error al crear tenant');
    } finally {
      setSubmittingTenant(false);
    }
  };

  const handleToggleStatus = async (tenant: Tenant) => {
    try {
      await tenantApi.toggleStatus(tenant.id);
      loadTenants();
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : 'Error al cambiar estado');
    }
  };

  const handleDeleteTenant = async (tenant: Tenant) => {
    if (!confirm(`¿Eliminar tenant "${tenant.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await tenantApi.delete(tenant.id);
      setTenantSuccess(`Tenant "${tenant.name}" eliminado`);
      loadTenants();
    } catch (err) {
      setTenantError(err instanceof Error ? err.message : 'Error al eliminar tenant');
    }
  };

  // ===== USER ACTIONS =====
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserError('');
    setUserSuccess('');
    if (!userForm.tenantId) {
      setUserError('Selecciona un tenant');
      return;
    }
    setSubmittingUser(true);
    try {
      await adminUserApi.createUser(userForm);
      setUserSuccess(`✅ Usuario "${userForm.email}" creado como ${userForm.roleId === 1 ? 'Admin' : 'Supervisor'} en el tenant seleccionado`);
      setUserForm({
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        tenantId: '',
        roleId: 1,
      });
    } catch (err) {
      setUserError(err instanceof Error ? err.message : 'Error al crear usuario');
    } finally {
      setSubmittingUser(false);
    }
  };

  // ===== FILTER TENANTS =====
  const filteredTenants = tenants.filter(
    (t) =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.nit && t.nit.includes(searchQuery)),
  );

  if (!user?.superAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-8 bg-red-50 rounded-2xl border border-red-200">
          <i className="ri-shield-keyhole-line text-5xl text-red-400 mb-4 block" />
          <h3 className="text-xl font-bold text-red-700">Acceso Denegado</h3>
          <p className="text-red-500 mt-2">Esta sección es exclusiva para Super Administradores.</p>
        </div>
      </div>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-indigo-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <i className="ri-shield-star-line" />
            Panel Super Administrador
          </div>
          <h2 className="text-4xl font-bold text-slate-800 mb-2">Gestión de Plataforma</h2>
          <p className="text-lg text-slate-500">Administra tenants y crea usuarios administrativos</p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-xl shadow-sm border border-gray-200 p-1">
            <button
              onClick={() => setActiveTab('tenants')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'tenants'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-building-2-line text-lg" />
              Tenants
            </button>
            <button
              onClick={() => setActiveTab('create-user')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === 'create-user'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-user-add-line text-lg" />
              Crear Usuario
            </button>
          </div>
        </div>

        {/* Status Messages */}
        {tenantSuccess && (
          <div className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200 text-green-700 flex items-center gap-2">
            <i className="ri-check-double-line text-lg" />
            {tenantSuccess}
            <button onClick={() => setTenantSuccess('')} className="ml-auto text-green-500 hover:text-green-700">
              <i className="ri-close-line" />
            </button>
          </div>
        )}
        {tenantError && (
          <div className="mb-6 p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 flex items-center gap-2">
            <i className="ri-error-warning-line text-lg" />
            {tenantError}
            <button onClick={() => setTenantError('')} className="ml-auto text-red-500 hover:text-red-700">
              <i className="ri-close-line" />
            </button>
          </div>
        )}

        {/* ============ TAB: TENANTS ============ */}
        {activeTab === 'tenants' && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Toolbar */}
            <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o NIT..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <button
                onClick={() => setShowCreateTenant(!showCreateTenant)}
                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all shadow-sm font-medium"
              >
                <i className={showCreateTenant ? 'ri-close-line' : 'ri-add-line'} />
                {showCreateTenant ? 'Cancelar' : 'Nuevo Tenant'}
              </button>
            </div>

            {/* Create Tenant Form */}
            {showCreateTenant && (
              <div className="p-6 bg-indigo-50/50 border-b border-indigo-100">
                <h4 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="ri-building-2-line text-indigo-600" />
                  Nuevo Tenant (Empresa)
                </h4>
                <form onSubmit={handleCreateTenant} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                      Nombre de la Empresa <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      minLength={2}
                      maxLength={255}
                      value={tenantForm.name}
                      onChange={(e) => setTenantForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Ej: Pool Services SAS"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">NIT</label>
                    <input
                      maxLength={50}
                      value={tenantForm.nit}
                      onChange={(e) => setTenantForm((p) => ({ ...p, nit: e.target.value }))}
                      placeholder="Ej: 900123456-7"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      type="submit"
                      disabled={submittingTenant}
                      className="w-full px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all font-medium disabled:opacity-50"
                    >
                      {submittingTenant ? (
                        <><i className="ri-loader-line animate-spin mr-2" />Creando...</>
                      ) : (
                        <><i className="ri-save-line mr-2" />Crear Tenant</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Tenant Table */}
            {loadingTenants ? (
              <div className="flex items-center justify-center py-16">
                <i className="ri-loader-line animate-spin text-3xl text-indigo-600 mr-3" />
                <span className="text-slate-500 text-lg">Cargando tenants...</span>
              </div>
            ) : filteredTenants.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <i className="ri-building-2-line text-5xl mb-3" />
                <p className="text-lg">No se encontraron tenants</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Empresa</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">NIT</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Estado</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Creado</th>
                      <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredTenants.map((tenant) => (
                      <tr key={tenant.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tenant.active ? 'bg-indigo-100' : 'bg-gray-100'}`}>
                              <i className={`ri-building-2-line text-lg ${tenant.active ? 'text-indigo-600' : 'text-gray-400'}`} />
                            </div>
                            <span className="font-semibold text-slate-800">{tenant.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{tenant.nit || '—'}</td>
                        <td className="px-6 py-4">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                            {tenant.id.slice(0, 8)}...
                          </code>
                          <button
                            onClick={() => { navigator.clipboard.writeText(tenant.id); setTenantSuccess(`ID copiado: ${tenant.id}`); }}
                            className="ml-1 text-gray-400 hover:text-indigo-600 transition-colors"
                            title="Copiar ID completo"
                          >
                            <i className="ri-file-copy-line text-sm" />
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                            tenant.active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${tenant.active ? 'bg-green-500' : 'bg-red-500'}`} />
                            {tenant.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-500">
                          {new Date(tenant.createdAt).toLocaleDateString('es-CO')}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleToggleStatus(tenant)}
                              className={`p-2 rounded-lg transition-colors ${
                                tenant.active
                                  ? 'text-amber-600 hover:bg-amber-50'
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={tenant.active ? 'Desactivar' : 'Activar'}
                            >
                              <i className={tenant.active ? 'ri-toggle-line text-lg' : 'ri-toggle-fill text-lg'} />
                            </button>
                            <button
                              onClick={() => handleDeleteTenant(tenant)}
                              className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                              title="Eliminar"
                            >
                              <i className="ri-delete-bin-line text-lg" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer info */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
              <span>{filteredTenants.length} tenant{filteredTenants.length !== 1 && 's'}</span>
              <button onClick={loadTenants} className="flex items-center gap-1 text-indigo-600 hover:underline">
                <i className="ri-refresh-line" /> Recargar
              </button>
            </div>
          </div>
        )}

        {/* ============ TAB: CREATE USER ============ */}
        {activeTab === 'create-user' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <i className="ri-user-add-line text-2xl text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800">Crear Usuario Administrador</h3>
                  <p className="text-sm text-slate-500">El admin podrá gestionar técnicos y piscinas de su tenant</p>
                </div>
              </div>

              {userSuccess && (
                <div className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200 text-green-700 flex items-center gap-2">
                  <i className="ri-check-double-line text-lg" />
                  {userSuccess}
                  <button onClick={() => setUserSuccess('')} className="ml-auto">
                    <i className="ri-close-line" />
                  </button>
                </div>
              )}
              {userError && (
                <div className="mb-6 p-4 bg-red-50 rounded-xl border border-red-200 text-red-700 flex items-center gap-2">
                  <i className="ri-error-warning-line text-lg" />
                  {userError}
                  <button onClick={() => setUserError('')} className="ml-auto">
                    <i className="ri-close-line" />
                  </button>
                </div>
              )}

              <form onSubmit={handleCreateUser} className="space-y-5">
                {/* Tenant Selection */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Tenant (Empresa) <span className="text-red-500">*</span>
                  </label>
                  <select
                    required
                    value={userForm.tenantId}
                    onChange={(e) => setUserForm((p) => ({ ...p, tenantId: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                  >
                    <option value="">Seleccionar tenant...</option>
                    {tenants.filter((t) => t.active).map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} {t.nit ? `(${t.nit})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Rol <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={userForm.roleId}
                    onChange={(e) => setUserForm((p) => ({ ...p, roleId: parseInt(e.target.value) }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                  >
                    <option value={1}>Admin — Acceso total al tenant</option>
                    <option value={3}>Supervisor — Ver y crear registros</option>
                    <option value={2}>Técnico — Solo crear mediciones</option>
                    <option value={4}>Visualizador — Solo lectura</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                      Nombre <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      value={userForm.firstName}
                      onChange={(e) => setUserForm((p) => ({ ...p, firstName: e.target.value }))}
                      placeholder="Nombre"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                      Apellido <span className="text-red-500">*</span>
                    </label>
                    <input
                      required
                      value={userForm.lastName}
                      onChange={(e) => setUserForm((p) => ({ ...p, lastName: e.target.value }))}
                      placeholder="Apellido"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Username <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    value={userForm.username}
                    onChange={(e) => setUserForm((p) => ({ ...p, username: e.target.value }))}
                    placeholder="Ej: admin_empresa"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm((p) => ({ ...p, email: e.target.value }))}
                    placeholder="admin@empresa.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">
                    Contraseña <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="password"
                    minLength={6}
                    value={userForm.password}
                    onChange={(e) => setUserForm((p) => ({ ...p, password: e.target.value }))}
                    placeholder="Mínimo 6 caracteres"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submittingUser}
                  className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingUser ? (
                    <><i className="ri-loader-line animate-spin mr-2" />Creando usuario...</>
                  ) : (
                    <><i className="ri-user-add-line mr-2" />Crear Usuario</>
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
